import React, { useCallback, useState } from 'react';

import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  FormControl,
  Typography,
  TextField,
  Button,
  Dialog,
  IconButton,
} from '@mui/material';

import { batchCreateFlags } from 'api';
import { useSnackContext } from 'hooks/useSnackContext';
import { ReviewedFullCandidateProps } from 'types';

import {
  ButtonWrapper,
  ContentWrapper,
  CloseWrapper,
} from './styledComponents';

export interface AddFlagProps {
  open: boolean;
  checkedCandidates: Set<number>;
  updateData: (updateData: ReviewedFullCandidateProps) => void;
  handleClose: () => void;
  setCheckedCandidates: React.Dispatch<React.SetStateAction<Set<number>>>;
}
export const AddAdminFlag: React.FC<AddFlagProps> = (props) => {
  const {
    open,
    checkedCandidates,
    setCheckedCandidates,
    handleClose,
    updateData,
  } = props;
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({
    noteError: false,
  });
  const checkedCandidateCount = checkedCandidates.size;

  const { setSnack } = useSnackContext();

  const addFlags = useCallback(
    async (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        if (!note) {
          return setErrorState({
            noteError: !note,
          });
        }
        setLoading(true);
        const { data } = await batchCreateFlags(
          Array.from(checkedCandidates.values()),
          {
            type: 'admin_flag',
            message: note,
          },
        );
        data?.forEach((candidate) => {
          updateData(candidate);
        });
        setCheckedCandidates(new Set());
        handleClose();
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed To Add Flag',
          error: e as Error,
        }));
      } finally {
        setLoading(false);
      }
    },
    [
      checkedCandidates,
      handleClose,
      note,
      setCheckedCandidates,
      setSnack,
      updateData,
    ],
  );

  return (
    <Dialog open={open}>
      <CloseWrapper>
        <IconButton
          sx={{ alignSelf: 'right' }}
          aria-label='close modal'
          component='span'
          onClick={handleClose}
        >
          <Close />
        </IconButton>
      </CloseWrapper>
      <ContentWrapper>
        <Typography variant='h5' sx={{ fontWeight: 'bold' }}>
          {`You are adding a admin flag to ${checkedCandidateCount} ${
            checkedCandidateCount === 1 ? 'Candidate' : 'Candidates'
          }`}{' '}
        </Typography>

        <FormControl sx={{ mt: 2 }}>
          <Typography sx={{ mt: 1 }} variant='h5'>
            Add Note
          </Typography>
          <TextField
            multiline
            onChange={(e) => {
              if (errorState.noteError) {
                setErrorState((prev) => ({ ...prev, noteError: false }));
              }
              const { value } = e.currentTarget;
              setNote(value);
            }}
            value={note}
            error={errorState.noteError}
            helperText={errorState.noteError && 'You must include a note'}
          />
          <ButtonWrapper>
            <Button
              sx={{ mt: 2, mr: 1 }}
              onClick={handleClose}
              variant='outlined'
            >
              CANCEL
            </Button>
            <LoadingButton
              sx={{ mt: 2, ml: 1 }}
              disabled={errorState.noteError}
              loading={loading}
              onClick={addFlags}
              variant='contained'
            >
              {checkedCandidateCount === 1 ? 'ADD FLAG' : 'ADD FLAGS'}
            </LoadingButton>
          </ButtonWrapper>
        </FormControl>
      </ContentWrapper>
    </Dialog>
  );
};
